import {useRef} from 'react';
import {connect} from 'react-redux';
import {createStructuredSelector} from 'reselect';

import uamTimeouts from '../../../consts/config/uamTimeouts';
import useDidUpdate from '../../../hooks/useDidUpdate';
import iotDeviceSettingsService from '../../../services/iotDevice/iotDeviceSettingsService';
import uamClientService from '../../../services/uam/uamClientService';
import yapService from '../../../services/yap/yapService';
import {setIsYapDataCollectionAllowed} from '../../../state/ducks/yapEncrypted';
import {makeSelectIotDeviceFullData} from '../../../state/selectors/consumer';
import {makeSelectIsDeviceReady, selectIsFirmwareInProgress} from '../../../state/selectors/iotDevice';
import {
    makeSelectIsDeviceSyncInProgress,
    makeSelectIsYapAutoCleaningInProgress,
    makeSelectIsYapDeviceInfoReceived,
    makeSelectIsYapFirmwareInProgress,
    makeSelectIsYapHolderInfoReceived,
    makeSelectIsYapSynchronizeDataCollectionInProgress,
    makeSelectIsYapSynchronizeDataCollectionReceived,
    makeSelectIsYapUpdateSettingsInProgress,
} from '../../../state/selectors/yapEncrypted';
import {dispatch} from '../../../state/store';

const mapStateToProps = createStructuredSelector({
    isYapDeviceInfoReceived: makeSelectIsYapDeviceInfoReceived(),
    isYapHolderInfoReceived: makeSelectIsYapHolderInfoReceived(),
    iotProduct: makeSelectIotDeviceFullData(),
    isDeviceReady: makeSelectIsDeviceReady(),
    isDeviceSyncInProgress: makeSelectIsDeviceSyncInProgress(),
    isHolderSyncInProgress: makeSelectIsDeviceSyncInProgress(true),
    isAutoCleaningInProgress: makeSelectIsYapAutoCleaningInProgress(),
    isYapUpdateSettingsInProgress: makeSelectIsYapUpdateSettingsInProgress(),
    isYapHolderUpdateSettingsInProgress: makeSelectIsYapUpdateSettingsInProgress(true),
    isYapFirmwareInProgress: makeSelectIsYapFirmwareInProgress(),
    isYapSynchronizeDeviceDataCollectionReceived: makeSelectIsYapSynchronizeDataCollectionReceived(),
    isYapSynchronizeDataCollectionInProgress: makeSelectIsYapSynchronizeDataCollectionInProgress(),
    isFirmwareInProgress: selectIsFirmwareInProgress,
});

const DataCollectionHandler = ({
    isYapDeviceInfoReceived,
    isYapHolderInfoReceived,
    isYapSynchronizeDeviceDataCollectionReceived,
    iotProduct,
    isDeviceReady,
    isDeviceSyncInProgress,
    isHolderSyncInProgress,
    isAutoCleaningInProgress,
    isYapUpdateSettingsInProgress,
    isYapHolderUpdateSettingsInProgress,
    isYapFirmwareInProgress,
    isYapSynchronizeDataCollectionInProgress,
    isFirmwareInProgress,
}) => {
    const {device} = iotProduct || {};
    const {is_device_in_sbl_mode, isHolderConnected} = device || {};
    const isHolderInSBLMode = iotDeviceSettingsService.isHolderInSBLMode(iotProduct);

    const isDataCollectionEnabled = yapService.isDataCollectionEnabled();
    const dataCollectionDelayTimer = useRef(false);
    const isStartDeviceDataCollection =
        isDataCollectionEnabled &&
        isDeviceReady &&
        isYapDeviceInfoReceived &&
        !isDeviceSyncInProgress &&
        !isHolderSyncInProgress &&
        !isAutoCleaningInProgress &&
        !isYapUpdateSettingsInProgress &&
        !isYapHolderUpdateSettingsInProgress &&
        !isYapFirmwareInProgress &&
        !isFirmwareInProgress &&
        !isYapSynchronizeDeviceDataCollectionReceived &&
        !is_device_in_sbl_mode;

    const isStartHolderDataCollection =
        isDataCollectionEnabled &&
        isDeviceReady &&
        isYapDeviceInfoReceived &&
        !isDeviceSyncInProgress &&
        !isHolderSyncInProgress &&
        !isAutoCleaningInProgress &&
        !isYapUpdateSettingsInProgress &&
        !isYapHolderUpdateSettingsInProgress &&
        !isYapFirmwareInProgress &&
        !isFirmwareInProgress &&
        isYapHolderInfoReceived &&
        isHolderConnected &&
        isYapSynchronizeDeviceDataCollectionReceived &&
        !isHolderInSBLMode;

    const isStopDeviceDataCollection =
        !isYapSynchronizeDeviceDataCollectionReceived &&
        (!isDataCollectionEnabled ||
            !isDeviceReady ||
            isDeviceSyncInProgress ||
            isHolderSyncInProgress ||
            isAutoCleaningInProgress ||
            isYapUpdateSettingsInProgress ||
            isYapHolderUpdateSettingsInProgress ||
            isYapFirmwareInProgress ||
            isFirmwareInProgress ||
            is_device_in_sbl_mode);

    const isStopHolderDataCollection =
        isYapSynchronizeDeviceDataCollectionReceived &&
        (!isDataCollectionEnabled ||
            !isDeviceReady ||
            !isHolderConnected ||
            isDeviceSyncInProgress ||
            isHolderSyncInProgress ||
            isAutoCleaningInProgress ||
            isYapUpdateSettingsInProgress ||
            isYapHolderUpdateSettingsInProgress ||
            isYapFirmwareInProgress ||
            isFirmwareInProgress ||
            isHolderInSBLMode);

    useDidUpdate(async () => {
        clearTimeout(dataCollectionDelayTimer.current);
        if (isStartDeviceDataCollection || isStartHolderDataCollection) {
            dataCollectionDelayTimer.current = setTimeout(async () => {
                dispatch(setIsYapDataCollectionAllowed(true));

                await uamClientService.proceedSynchronizeAllData(isStartHolderDataCollection, false);
            }, uamTimeouts.UAM_DATA_COLLECTION_BEFORE_START_TIMEOUT * 1000);
        }
    }, [isStartDeviceDataCollection, isStartHolderDataCollection]);

    useDidUpdate(async () => {
        if (isStopDeviceDataCollection || isStopHolderDataCollection) {
            clearTimeout(dataCollectionDelayTimer.current);
            dispatch(setIsYapDataCollectionAllowed(false));
            await uamClientService.cancelSynchronizeData();
        }
    }, [isStopDeviceDataCollection, isStopHolderDataCollection, isYapSynchronizeDataCollectionInProgress]);

    return null;
};

export default connect(mapStateToProps)(DataCollectionHandler);
